import { useMemo } from "react";
import {
  DEFAULT_ORDER_NUMBER_TEMPLATE,
  ORDER_NUMBER_TEMPLATE_PRESETS,
  ORDER_NUMBER_TEMPLATE_TOKENS,
  formatOrderNumber,
  orderNumberScopeKey,
  parseOrderNumberTemplate,
  previewOrderNumber,
} from "@gaole/shared";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Button, Textarea } from "../../components/ui";

export function OrderNumberSettingsEditor({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (template: string) => void;
}) {
  const template = String(value ?? DEFAULT_ORDER_NUMBER_TEMPLATE);
  const parsed = useMemo(() => parseOrderNumberTemplate(template), [template]);
  const scopeKey = useMemo(
    () => (parsed.ok ? orderNumberScopeKey(template) : "global"),
    [parsed.ok, template],
  );

  const previewQuery = useQuery({
    queryKey: ["order-number-preview", template],
    queryFn: () =>
      api<{ preview: string }>(
        `/settings/order-number-preview?template=${encodeURIComponent(template)}`,
      ),
    enabled: parsed.ok,
  });

  const localPreview = parsed.ok ? previewOrderNumber(template, 41) : null;
  const preview = previewQuery.data?.preview ?? localPreview;

  return (
    <div className="space-y-4">
      <Textarea
        label="Order number template"
        rows={2}
        value={template}
        onChange={(e) => onChange(e.target.value)}
        hint="Must include {seq} or {seq:N}. Date tokens reset the counter per day, month, or year."
      />

      {!parsed.ok && <p className="text-xs text-red-300">{parsed.error}</p>}

      <div className="flex flex-wrap gap-2">
        {ORDER_NUMBER_TEMPLATE_PRESETS.map((preset) => (
          <Button
            key={preset.template}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange(preset.template)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="rounded-xl border border-line bg-surface-2 p-3 text-xs text-muted">
        <p className="mb-2 font-semibold uppercase tracking-widest text-foreground">Tokens</p>
        <ul className="space-y-1">
          {ORDER_NUMBER_TEMPLATE_TOKENS.map((item) => (
            <li key={item.token}>
              <code className="text-brand-pink">{item.token}</code>
              <span className="text-muted"> — {item.description}</span>
            </li>
          ))}
        </ul>
      </div>

      {parsed.ok && (
        <div className="rounded-xl border border-line bg-surface-2 p-3 text-sm">
          <p className="text-xs text-muted">Next number preview</p>
          <p className="mt-1 font-mono text-base font-semibold text-brand-pink">{preview ?? "…"}</p>
          <p className="mt-1 text-[10px] text-muted">
            Counter scope: <span className="font-mono">{scopeKey}</span>
            {previewQuery.isFetching ? " · refreshing…" : null}
          </p>
          <p className="mt-2 text-[10px] text-muted">
            Example with seq 42:{" "}
            <span className="font-mono text-foreground">{formatOrderNumber(template, 42)}</span>
          </p>
        </div>
      )}
    </div>
  );
}
